// HotMaps tracker — runs on customer sites. Goals, in priority order:
//   1. Do nothing the host page would feel. No layout work, no sync XHR,
//      no event handler that blocks. Everything routes through idle/microtask
//      or sendBeacon.
//   2. Collect what's needed for heatmaps + session replay: clicks, throttled
//      mouse moves, scroll, viewport, page views, and rrweb DOM snapshots.
//   3. Batch and ship. One POST per ~5s or ~50KB, whichever first. On unload
//      we flush via navigator.sendBeacon so we don't lose the last burst.
//
// Build-time defines (see build.mjs):
//   __HOTMAPS_INGEST__   absolute URL of the ingest worker, e.g. "https://ingest.hotmaps.dev"
//   __HOTMAPS_VERSION__  semver string of the tracker build
//
// At runtime the embedding tag controls config:
//   <script async src="https://cdn.hotmaps.dev/t.js"
//           data-site="{siteId}"></script>
// One URL, served to every customer; site identity travels on the
// `data-site` attribute. The bundle reads it at boot and ships it as `s`
// on every IngestBatch (protocol v=2). This keeps the bundle response
// byte-identical across customers → CDN/browser cache is maximally effective.
//
// Backward compat: if `data-site` is missing (legacy install or local build),
// we fall back to location.hostname and the ingest worker resolves the site
// via its hostname→id KV map (protocol v=1). `data-site-domain` on the
// <script> tag still overrides the hostname for QA scenarios.

import { record } from "rrweb";
import type {
  IngestBatch,
  TrackerEvent,
  ClickEvent,
  MoveSampleEvent,
  ScrollEvent,
  PageViewEvent,
  RrwebChunkEvent,
  ResizeEvent,
  TouchEvent as HmTouchEvent,
  FormInteractionEvent,
  PerformanceEvent,
  CustomEvent as HmCustomEvent,
  IdentifyEvent,
} from "@hotmaps/shared/events";
import { PROTOCOL_VERSION } from "@hotmaps/shared/events";
import { registerSignalDetectors } from "./signals.js";
import { registerErrorCapture } from "./errors.js";
import { registerConsoleCapture } from "./console.js";
import { registerRiskEngine } from "./risk.js";

declare const __HOTMAPS_INGEST__: string;
declare const __HOTMAPS_VERSION__: string;

// --- public SDK shape exposed on window.hotmaps -----------------------------
type PublicSDK = {
  /** Fire a custom event from the host page. */
  track(name: string, props?: Record<string, string | number | boolean>): void;
  /**
   * Set the visitor's lifecycle stage. One of:
   * visitor · interested · cart · checkout · purchase · returning.
   * Filterable in the dashboard / `GET /visitors?status=<stage>`.
   */
  stage(name: string): void;
  /** Associate the current visitor with a stable customer-supplied id. */
  identify(uid: string, traits?: Record<string, string | number | boolean>): void;
  /** Returns true after boot completes. */
  ready(): boolean;
  /**
   * Force-send everything buffered NOW (incl. pending rrweb + a fresh snapshot).
   * Call this from a native host on background/pause (Android onStop, iOS
   * didEnterBackground) so a WebView being torn down never strands the last
   * burst or FullSnapshot. Safe to call anytime; a no-op if nothing is pending.
   */
  flush(): void;
  /**
   * Current recording session id — the `sid` on every ingest batch, and the
   * `:sessionId` in the dashboard/API replay + signals endpoints. Lets host
   * tooling (e.g. a bug reporter) link its own records to this recording.
   */
  getSessionId(): string;
  /** Stable per-browser visitor id (localStorage-backed). */
  getVisitorId(): string;
};

// --- guard against double-loading -------------------------------------------
const W = window as unknown as {
  __hotmapsLoaded?: boolean;
  hotmaps?: PublicSDK;
  /** Queued calls made before the SDK booted, executed on boot. */
  __hotmapsQueue?: Array<{ m: "track" | "identify" | "stage"; a: unknown[] }>;
};
if (W.__hotmapsLoaded) {
  // Already running on this page. Bail silently.
} else {
  W.__hotmapsLoaded = true;
  // CMP gate (R2): when the site runs our consent module, the loader stamps
  // data-consent="1" on this tag. Recording must NOT start until the visitor
  // grants the "statistics" category — zero events to ingest before consent.
  // consent.js persists the state (hm_consent_v1) and fires "hm:consent".
  if (consentGateActive() && !statisticsGranted()) {
    const onGrant = () => {
      if (!statisticsGranted()) return;
      window.removeEventListener("hm:consent", onGrant);
      window.removeEventListener("storage", onGrant);
      boot();
    };
    window.addEventListener("hm:consent", onGrant);
    window.addEventListener("storage", onGrant); // cross-tab grants
  } else {
    boot();
  }
}

// True when the loader marked our tag with the consent gate. currentScript is
// valid here (synchronous top-level execution); the selector scan covers
// re-execution edge cases where it's null.
function consentGateActive(): boolean {
  try {
    const cur = document.currentScript as HTMLScriptElement | null;
    if (cur?.dataset.consent === "1") return true;
    const tags = document.querySelectorAll<HTMLScriptElement>('script[data-consent="1"]');
    for (let i = 0; i < tags.length; i++) {
      if (/\/t\.js/.test(tags[i]?.src || "")) return true;
    }
  } catch { /* DOM unavailable */ }
  return false;
}
function statisticsGranted(): boolean {
  try {
    const raw = localStorage.getItem("hm_consent_v1");
    if (!raw) return false;
    const st = JSON.parse(raw) as { v?: number; cats?: { statistics?: boolean } };
    return !!(st && st.v === 1 && st.cats && st.cats.statistics === true);
  } catch { return false; }
}

function boot() {
  // --- config -----------------------------------------------------------
  const scriptEl = document.currentScript as HTMLScriptElement | null;
  const domainOverride = scriptEl?.dataset.siteDomain;
  const domain = domainOverride || location.hostname;
  // Site identity comes from the script tag's data-site attribute. We try
  // document.currentScript first (works for HTML-embedded async scripts);
  // if that's null (the script was injected dynamically, e.g. via GTM, so
  // currentScript is not defined when our code runs), we fall back to
  // scanning every <script data-site> tag and picking one whose src looks
  // like the tracker. When neither yields an id we ship v=1 and let
  // ingest fall back to hostname lookup.
  const siteId = resolveSiteId(scriptEl);
  const visitorId = getOrCreateVisitorId();
  // ONE session spans the whole visit: entry → page navigations → tab-switch
  // away and back → until the visitor leaves or is idle for INACTIVITY_MIN.
  // A fresh UUID per page load (the old behavior) fragmented every WordPress
  // navigation into its own "session" — 60% single-page junk. See getSession().
  const { id: sessionId } = getOrResumeSession();
  const sessionStart = Date.now();
  let pageViewId = uuid();
  let pageStart = sessionStart;

  // Throttle limits — picked to keep payloads small.
  const MOUSE_SAMPLE_MS = 120;       // ~8 samples/sec
  const SCROLL_SAMPLE_MS = 150;
  const FLUSH_INTERVAL_MS = 5000;    // 5s — live view / fresh data responsiveness.
                                     // (A 10s trial felt laggy on the live dashboard;
                                     // the big cost wins are storage + bot-skip, not this.)
  const FLUSH_BYTE_BUDGET = 48 * 1024;
  const RRWEB_CHUNK_MS = 2000;       // emit rrweb chunks every 2s
  // Hard per-session replay cap. Runaway pages (canvas loops, infinite scroll,
  // SPAs that re-snapshot) can balloon a single replay to gigabytes. Past this
  // many rrweb bytes we STOP replay capture — clicks/scroll/signals/heatmap keep
  // flowing, so analytics stay complete; only the video is bounded.
  const REPLAY_BYTE_CAP = 80 * 1024 * 1024; // 80MB

  // --- session-level state (v3) ----------------------------------------
  // identifiedUid is set once the customer calls hotmaps.identify(uid).
  // It rides on every subsequent batch envelope (cheap, ~30 bytes) so the
  // server can attribute every batch in the session, not just the one
  // carrying the IdentifyEvent itself.
  let identifiedUid: string | undefined;
  // maxScrollPct tracks the deepest point the user reached on the current
  // page-view, as a percentage of document height. Reset on SPA nav.
  let maxScrollPct = 0;

  // --- buffer / dispatch ------------------------------------------------
  let buf: TrackerEvent[] = [];
  let bufBytes = 0;
  let flushTimer: number | undefined;

  function relTs(): number {
    return Date.now() - sessionStart;
  }

  function push(ev: TrackerEvent) {
    buf.push(ev);
    // crude byte estimate — JSON.stringify each event is O(n) which is fine
    // at the volumes we see, but for hot paths we approximate.
    bufBytes += approxSize(ev);
    if (bufBytes >= FLUSH_BYTE_BUDGET) flush("budget");
  }

  function flush(reason: "timer" | "budget" | "unload" | "nav") {
    if (buf.length === 0) return;
    // Heartbeat the session's last-activity stamp on every flush (≤5s while the
    // visitor is doing anything), so navigation and tab-return inside the idle
    // window resume THIS session instead of starting a new one.
    markSessionActive(sessionId, Date.now());
    // Ship v=3 with `s` and (optionally) `uid` when we know the site id;
    // otherwise v=1 so legacy hostname-resolved sites keep working.
    const batch: IngestBatch = siteId
      ? {
          v: PROTOCOL_VERSION,
          s: siteId,
          d: domain,
          sid: sessionId,
          vid: visitorId,
          ...(identifiedUid ? { uid: identifiedUid } : {}),
          pid: pageViewId,
          pageHash: hashPath(location.pathname),
          pathname: location.pathname,
          start: sessionStart,
          events: buf,
        }
      : {
          v: 1,
          d: domain,
          sid: sessionId,
          vid: visitorId,
          pid: pageViewId,
          pageHash: hashPath(location.pathname),
          pathname: location.pathname,
          start: sessionStart,
          events: buf,
        };
    buf = [];
    bufBytes = 0;

    const body = JSON.stringify(batch);
    // sendBeacon: fire-and-forget, survives page unload, never blocks. Falls
    // back to keepalive fetch if unavailable or rejected (rare).
    const url = __HOTMAPS_INGEST__ + "/e";
    if (reason === "unload" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    try {
      // NOTE: no `keepalive: true` here. keepalive caps the request body at
      // 64KB (browser limit), which silently stranded the first batch of each
      // session — the one carrying rrweb's multi-MB FullSnapshot — so replays
      // arrived with only incremental events and couldn't render. Unload
      // delivery is handled separately by navigator.sendBeacon above; regular
      // timer/budget/nav flushes use a normal fetch with no size cap.
      fetch(url, {
        method: "POST",
        body,
        headers: { "content-type": "application/json" },
        credentials: "omit",
        mode: "cors",
      })
        .then((res) => {
          // Free piggyback for the unified loader: surface the server's current
          // module revision so d.js can reconcile live (mount/unmount a product
          // mid-session) without ever making its own request.
          try {
            const rev = res.headers.get("x-hm-rev");
            if (rev !== null) {
              window.dispatchEvent(new CustomEvent("hm:rev", { detail: Number(rev) }));
            }
          } catch {
            /* CustomEvent unsupported — loader still refreshes on next pageview */
          }
        })
        .catch(() => {
          // Swallow — host site shouldn't see our network errors. We could
          // retry on next flush by re-buffering, but for MVP we drop.
        });
    } catch {
      /* noop */
    }
  }

  function scheduleFlush() {
    if (flushTimer !== undefined) return;
    flushTimer = window.setTimeout(() => {
      flushTimer = undefined;
      flush("timer");
      scheduleFlush();
    }, FLUSH_INTERVAL_MS);
  }
  scheduleFlush();

  // --- event capture: page view ----------------------------------------
  // We mark the FIRST page view of the session as `first: true`; on it we
  // also include UTM params parsed from location.search. The aggregator
  // uses both to populate session-level source attribution. Subsequent PVs
  // (SPA navigations, follow-on pages) omit the source data.
  let firstPv = true;
  function emitPageView() {
    const ev: PageViewEvent = {
      t: "pv",
      ts: relTs(),
      url: location.pathname + location.search,
      title: document.title,
      ref: document.referrer || undefined,
      vw: window.innerWidth,
      vh: window.innerHeight,
      dw: documentWidth(),
      dh: documentHeight(),
      ua: navigator.userAgent,
    };
    if (firstPv) {
      ev.first = true;
      const utm = readUtmParams();
      if (utm) ev.utm = utm;
      ev.cl = readClientInfo();
      firstPv = false;
    }
    push(ev);
  }
  emitPageView();

  function readClientInfo(): PageViewEvent["cl"] {
    const cl: NonNullable<PageViewEvent["cl"]> = {};
    try {
      const nav = navigator as Navigator & {
        deviceMemory?: number;
        connection?: { effectiveType?: string };
        userAgentData?: { platform?: string };
      };
      if (typeof screen !== "undefined") {
        if (screen.width) cl.sw = screen.width;
        if (screen.height) cl.sh = screen.height;
      }
      if (window.devicePixelRatio) cl.dpr = Math.round(window.devicePixelRatio * 100) / 100;
      if (nav.language) cl.lang = nav.language;
      if (nav.languages && nav.languages.length) cl.langs = nav.languages.slice(0, 5).join(",");
      try { cl.tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* unsupported */ }
      cl.plat = nav.userAgentData?.platform || nav.platform || undefined;
      if (typeof nav.hardwareConcurrency === "number") cl.cores = nav.hardwareConcurrency;
      if (typeof nav.deviceMemory === "number") cl.mem = nav.deviceMemory;
      if (nav.connection?.effectiveType) cl.conn = nav.connection.effectiveType;
      if (typeof nav.maxTouchPoints === "number") cl.touch = nav.maxTouchPoints;
    } catch {
      /* defensive — never break the host page over telemetry */
    }
    return cl;
  }

  function readUtmParams(): PageViewEvent["utm"] | null {
    try {
      const sp = new URLSearchParams(location.search);
      const out: NonNullable<PageViewEvent["utm"]> = {};
      const map: Array<[string, keyof NonNullable<PageViewEvent["utm"]>]> = [
        ["utm_source", "source"],
        ["utm_medium", "medium"],
        ["utm_campaign", "campaign"],
        ["utm_term", "term"],
        ["utm_content", "content"],
      ];
      let any = false;
      for (const [key, field] of map) {
        const v = sp.get(key);
        if (v && v.length <= 200) {
          out[field] = v;
          any = true;
        }
      }
      return any ? out : null;
    } catch {
      return null;
    }
  }

  // SPA navigation: hook pushState/replaceState + popstate so we still get
  // page views for client-side routes.
  hookHistory(() => {
    // Flush before page change so events stay attached to the previous path.
    flush("nav");
    pageViewId = uuid();
    pageStart = Date.now();
    maxScrollPct = 0;        // reset per-page scroll depth
    // Reset the risk engine's per-page transient state (scroll depth, gesture
    // refractories) so a deep scroll on the prior route + a scroll-to-top on
    // the new one doesn't fire a phantom scroll_reversal. `risk` is created
    // later in boot(); nav only fires after boot completes, so it's defined.
    try { risk.resetPage(); } catch { /* risk not ready yet — nothing to reset */ }
    emitPageView();
  });

  // --- event capture: clicks -------------------------------------------
  document.addEventListener(
    "click",
    (e) => {
      // Use ev.composedPath()[0] when target is inside shadow DOM. For MVP
      // we accept .target; refine when we hit a shadow-DOM-heavy customer.
      const target = e.target as Element | null;
      const ev: ClickEvent = {
        t: "c",
        ts: relTs(),
        x: e.clientX,
        y: e.clientY,
        px: e.clientX + window.scrollX,
        py: e.clientY + window.scrollY,
        vw: window.innerWidth,
        vh: window.innerHeight,
        dw: documentWidth(),
        dh: documentHeight(),
        tag: target?.tagName.toLowerCase(),
        sel: target ? shortSelector(target) : undefined,
        txt: target ? truncatedText(target) : undefined,
      };
      push(ev);
    },
    { capture: true, passive: true }
  );

  // --- event capture: mouse moves (throttled) --------------------------
  let lastMoveAt = 0;
  document.addEventListener(
    "mousemove",
    (e) => {
      const now = Date.now();
      if (now - lastMoveAt < MOUSE_SAMPLE_MS) return;
      lastMoveAt = now;
      const ev: MoveSampleEvent = {
        t: "m",
        ts: relTs(),
        x: e.clientX,
        y: e.clientY,
        px: e.clientX + window.scrollX,
        py: e.clientY + window.scrollY,
      };
      push(ev);
    },
    { capture: true, passive: true }
  );

  // --- event capture: scroll (throttled) + max-depth -------------------
  let lastScrollAt = 0;
  window.addEventListener(
    "scroll",
    () => {
      const now = Date.now();
      // Update max-scroll on every scroll, before the sample throttle. The
      // metric needs to know the deepest point regardless of sample rate.
      const dh = documentHeight();
      const reachedPx = window.scrollY + window.innerHeight;
      const pct = dh > 0 ? Math.min(100, Math.round((reachedPx / dh) * 100)) : 0;
      if (pct > maxScrollPct) maxScrollPct = pct;
      if (now - lastScrollAt < SCROLL_SAMPLE_MS) return;
      lastScrollAt = now;
      const ev: ScrollEvent = {
        t: "s",
        ts: relTs(),
        py: window.scrollY,
        vh: window.innerHeight,
        dh,
      };
      push(ev);
    },
    { capture: true, passive: true }
  );

  // --- event capture: resize -------------------------------------------
  let resizeTimer: number | undefined;
  window.addEventListener("resize", () => {
    // Debounce — resize fires continuously during drag.
    if (resizeTimer !== undefined) clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      const ev: ResizeEvent = {
        t: "rs",
        ts: relTs(),
        vw: window.innerWidth,
        vh: window.innerHeight,
      };
      push(ev);
    }, 200);
  });

  // --- rrweb DOM recording ---------------------------------------------
  // rrweb buffers internally; we flush its events into our buffer every
  // RRWEB_CHUNK_MS to keep memory bounded.
  let rrwebPending: unknown[] = [];
  // Live-mode: while ≥1 viewer is watching (via the live relay), tee every rrweb
  // event into a live buffer and stream it out. Off by default → zero overhead.
  let liveOn = false;
  let liveBuf: unknown[] = [];
  // Idle pause: a visitor who leaves the tab open with no interaction records
  // nothing but ambient DOM churn (ads, carousels) — pure dead-air storage
  // (we measured single sessions of 2+ GB / 160h). After IDLE_PAUSE_MS with no
  // human activity we STOP rrweb entirely; the next real interaction restarts
  // it with a fresh snapshot. Recording covers real human time only.
  const IDLE_PAUSE_MS = 15 * 60 * 1000; // 15 min — long enough for reading a page
  let recStop: (() => void) | null = null;
  let idlePaused = false;
  let lastActivityAt = Date.now();
  let replayBytes = 0;       // cumulative rrweb bytes shipped this session
  let replayCapped = false;  // true once REPLAY_BYTE_CAP is hit — replay stops, analytics continue
  const startRecording = () => {
    if (replayCapped) return; // never re-arm replay after the cap
    try {
      recStop = (record({
      emit(rrEvent) {
        rrwebPending.push(rrEvent);
        if (liveOn) liveBuf.push(rrEvent);
      },
      // Capture input content by default so the dashboard can surface what
      // visitors typed (emails, names, comments). This is a deliberate
      // product choice — see the conversation in CHANGELOG. A hard-block is
      // kept on <input type="password"> at the rrweb level so credentials
      // are NEVER captured, regardless of any site setting or future code
      // path. Site owners can still redact additional selectors via the
      // existing privacy-masks setting (CSS-based).
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // non-negotiable, do not change
      },
      // Inline external stylesheets so the FullSnapshot is self-contained.
      // Without this, the dashboard's snapshot iframe would try to fetch the
      // customer's CSS from its origin and fail on CORS/CSP grounds.
      inlineStylesheet: true,
      // Inline images so the snapshot looks right even if the original
      // assets are no longer reachable. Capped via the size limit below.
      collectFonts: true,
      // Take a fresh FullSnapshot every 5 min. Two wins: (1) resilience — if any
      // single snapshot batch is lost in transit the replay recovers at the next
      // checkpoint instead of being unplayable; (2) seeking — the player can jump
      // into a long session without replaying every mutation from t=0.
      checkoutEveryNms: 300_000,
      // Limit blob URL recording; canvas+media require explicit opt-in via
      // additional flags we add when the screenshot fallback isn't enough.
      sampling: {
        // Record mouse/touch movement into the replay (throttled ~20/s) so the
        // cursor is VISIBLE in playback. We still capture our own move events
        // separately for heatmaps; this is the visual track for the video.
        mousemove: 50,
        scroll: 150,
        input: "last",
      },
    }) as unknown as (() => void)) ?? null;
    } catch (err) {
      // rrweb can throw on extremely hostile environments (e.g. Trusted Types
      // CSP). Swallow — heatmap path still works without replay.
    }
  };
  startRecording();

  // Any genuine human signal resets the idle clock; if we had paused, resume
  // recording (rrweb re-takes a full snapshot on start, so the replay picks up
  // cleanly from the current DOM).
  const bumpActivity = () => {
    lastActivityAt = Date.now();
    if (idlePaused) { idlePaused = false; startRecording(); } // startRecording no-ops if capped
  };
  // Full activity taxonomy — EVERY form of a genuine human action, across all
  // device classes. Missing one would falsely pause a live visitor, so we err
  // toward completeness (each handler just stamps a timestamp — negligible):
  //   clicks (all)   → mousedown, click, dblclick, auxclick, contextmenu, pointerdown, pointerup
  //   scroll (all)   → scroll, wheel, touchmove (swipe/drag/pinch), keydown (space/arrows)
  //   pointer/move   → mousemove, pointermove
  //   touch          → touchstart (tap), touchmove, touchend
  //   keyboard       → keydown, keypress, keyup (incl. virtual keyboards)
  //   text/clipboard → input (paste/IME/autofill), change, copy, cut, paste, select
  //   focus          → focusin (tab into a field)
  // Any of these = a live human → keep recording. None for 15 min = dead air.
  ["mousemove", "mousedown", "click", "dblclick", "auxclick", "contextmenu",
   "pointerdown", "pointerup", "pointermove", "wheel", "scroll",
   "touchstart", "touchmove", "touchend", "keydown", "keypress", "keyup",
   "input", "change", "copy", "cut", "paste", "select", "focusin"].forEach((ev) =>
    window.addEventListener(ev, bumpActivity, { passive: true, capture: true }));
  document.addEventListener("visibilitychange", () => { if (!document.hidden) bumpActivity(); });

  // Idle watchdog: pause recording after IDLE_PAUSE_MS of no human activity.
  setInterval(() => {
    if (idlePaused) return;
    if (Date.now() - lastActivityAt >= IDLE_PAUSE_MS) {
      idlePaused = true;
      try { recStop && recStop(); } catch { /* ignore */ } finally { recStop = null; }
      flush("nav"); // ship whatever's buffered, then go quiet until activity
    }
  }, 60_000);

  // While paused, a cheap heartbeat keeps recording the FACT that the tab is
  // still open (last_event_at → open-duration) without recording DOM. Capped so
  // a tab abandoned for weeks doesn't ping forever — 48h makes the point.
  const HEARTBEAT_MS = 5 * 60 * 1000;
  const MAX_HEARTBEATS = (48 * 60) / 5; // 48h worth
  let heartbeats = 0;
  setInterval(() => {
    if (!idlePaused || heartbeats >= MAX_HEARTBEATS) return;
    heartbeats++;
    push({ t: "hb", ts: relTs(), idle: Date.now() - lastActivityAt });
    flush("nav");
  }, HEARTBEAT_MS);
  setInterval(() => {
    if (replayCapped || rrwebPending.length === 0) return;
    const chunk: RrwebChunkEvent = {
      t: "rr",
      ts: relTs(),
      events: rrwebPending,
    };
    rrwebPending = [];
    replayBytes += approxSize(chunk);
    push(chunk);
    if (replayBytes >= REPLAY_BYTE_CAP) {
      // Runaway replay — stop capture, keep analytics. rrweb's own recorder is
      // torn down; the buffered chunk above is the last video we ship.
      replayCapped = true;
      try { recStop && recStop(); } catch { /* ignore */ } finally { recStop = null; }
    }
  }, RRWEB_CHUNK_MS);

  // --- live relay: stream rrweb to viewers on-demand -------------------------
  // Derive the live worker origin from the ingest one (ingest.<z> → live.<z>).
  // Poll the per-session viewer count; while >0, flush the live buffer every 1s.
  // Nobody watching → nothing streams. Poll interval is tunable (load vs latency).
  const LIVE_BASE = (() => { try { return __HOTMAPS_INGEST__.replace("://ingest.", "://live."); } catch { return ""; } })();
  if (LIVE_BASE && LIVE_BASE !== __HOTMAPS_INGEST__) {
    const liveUrl = LIVE_BASE + "/sites/" + encodeURIComponent(siteId ?? "") + "/sessions/" + encodeURIComponent(sessionId ?? "") + "/live";
    // Poll the viewer count. Interval is a latency-vs-load trade-off: it bounds
    // how long an operator waits after clicking "watch live" before the stream
    // starts. 10s felt broken (the popup sat on a spinner); 3s starts the stream
    // fast while staying cheap (a tiny GET; nothing streams unless viewers > 0).
    const checkViewers = () => {
      fetch(liveUrl + "/status")
        .then((r) => r.json())
        .then((d: { viewers?: number }) => {
          const watching = !!d && typeof d.viewers === "number" && d.viewers > 0;
          if (watching && !liveOn) {
            liveOn = true;
            // Fresh FullSnapshot so a viewer joining mid-session renders at once.
            try { (record as unknown as { takeFullSnapshot?: (v?: boolean) => void }).takeFullSnapshot?.(true); } catch { /* ignore */ }
          } else if (!watching && liveOn) {
            liveOn = false;
            liveBuf = [];
          }
        })
        .catch(() => { /* relay unreachable → stay off */ });
    };
    setInterval(checkViewers, 3000);
    setInterval(() => {
      if (!liveOn || liveBuf.length === 0) return;
      const batch = liveBuf;
      liveBuf = [];
      fetch(liveUrl + "/publish", { method: "POST", body: JSON.stringify({ events: batch }) }).catch(() => { /* drop */ });
    }, 1000);
  }

  // --- field input capture (fi) ---------------------------------------
  // Path B Phase B: capture what visitors type so the dashboard can later
  // discover schemas, let owners tag specific fields (email, customer_id),
  // and auto-identify the visitor. Throttled per-element via blur or 1s
  // idle. Password types are a hard block — see the rrweb config above for
  // the matching guarantee at the replay layer.
  const FIELD_IDLE_MS = 1000;
  const FIELD_VALUE_CAP = 500;
  const FIELD_TEXT_CAP = 120;
  const fieldTimers = new WeakMap<Element, number>();
  const fieldLastValue = new WeakMap<Element, string>();
  // Dedupe by field SIGNATURE + value for the whole session, not just per element
  // object: SPA re-renders and full page loads create new element objects for
  // the same field, which re-sent the same value (16% of all field rows were
  // exact repeats). Only short hashes are kept — never raw values — in
  // sessionStorage, scoped to this session id so a new session starts clean.
  const FIELD_SEEN_KEY = "_hm_fs";
  const FIELD_SEEN_MAX = 300;
  let fieldSeen: string[] = [];
  try {
    const raw = JSON.parse(sessionStorage.getItem(FIELD_SEEN_KEY) || "null") as { s?: string; h?: string[] } | null;
    if (raw && raw.s === sessionId && Array.isArray(raw.h)) fieldSeen = raw.h.slice(-FIELD_SEEN_MAX);
  } catch { /* storage blocked → in-memory only */ }
  function fieldHash(sig: string, value: string): string {
    let h = 5381;
    const str = sig + "\u0000" + value;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  }
  function fieldAlreadySent(h: string): boolean { return fieldSeen.includes(h); }
  function markFieldSent(h: string): void {
    fieldSeen.push(h);
    if (fieldSeen.length > FIELD_SEEN_MAX) fieldSeen = fieldSeen.slice(-FIELD_SEEN_MAX);
    try { sessionStorage.setItem(FIELD_SEEN_KEY, JSON.stringify({ s: sessionId, h: fieldSeen })); } catch { /* ignore */ }
  }

  function shouldCaptureField(el: Element): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement) && !(el instanceof HTMLSelectElement)) {
      return false;
    }
    if (el instanceof HTMLInputElement) {
      const ty = (el.type || "text").toLowerCase();
      // Hard block: never capture password / credit-card-ish autocomplete
      // hints. These are deliberately conservative — owners can still
      // redact further via the existing maskSelectors privacy knob.
      if (ty === "password") return false;
      if ((el.autocomplete || "").toLowerCase().includes("cc-")) return false;
      if (ty === "hidden") return false;
      if (ty === "file") return false;
      // PCI safety: most checkouts use a plain type="text" card/CVV input with
      // NO cc-* autocomplete, so the check above misses them. Block any field
      // whose name/id/placeholder/label/autocomplete looks like a payment
      // field — never ship a PAN or CVV to ingest.
      if (PAYMENT_FIELD_RE.test(paymentBlob(el))) return false;
    }
    return true;
  }
  // Payment-field recognizer (he/en/ar) + a Luhn backstop on the value.
  const PAYMENT_FIELD_RE = /(card.?number|cardnum|ccnum|cc-?num|pan\b|creditcard|credit.?card|cvv|cvc|csc|cvn|security.?code|card.?code|expir|exp.?date|exp.?month|exp.?year|אשראי|כרטיס.?אשראי|מספר.?כרטיס|תוקף|קוד.?אבטחה|بطاقة|رقم.?البطاقة|الأمان|انتهاء|رمز.?التحقق)/i;
  function paymentBlob(el: HTMLInputElement): string {
    return [el.name, el.id, el.placeholder, el.getAttribute("aria-label"), el.autocomplete, el.getAttribute("inputmode")]
      .filter(Boolean).join(" ");
  }
  function looksLikeCardNumber(v: string): boolean {
    const d = v.replace(/[\s-]/g, "");
    if (!/^\d{13,19}$/.test(d)) return false;
    let sum = 0, alt = false;
    for (let i = d.length - 1; i >= 0; i--) {
      let n = d.charCodeAt(i) - 48;
      if (alt) { n *= 2; if (n > 9) n -= 9; }
      sum += n; alt = !alt;
    }
    return sum % 10 === 0; // Luhn-valid → treat as a card number, drop it
  }

  function findLabel(el: Element): string | null {
    // Prefer aria-label > <label for=...> > parent <label>'s text. Cap.
    const aria = el.getAttribute("aria-label");
    if (aria) return clip(aria, FIELD_TEXT_CAP);
    const id = el.id;
    if (id) {
      try {
        const lab = document.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (lab?.textContent) return clip(lab.textContent.trim(), FIELD_TEXT_CAP);
      } catch { /* invalid selector */ }
    }
    const parent = el.closest("label");
    if (parent?.textContent) return clip(parent.textContent.trim(), FIELD_TEXT_CAP);
    return null;
  }

  function clip(s: string, n: number): string {
    return s.length > n ? s.slice(0, n) : s;
  }

  // Quick-and-correct signature: sha1 isn't needed — we just want a stable
  // string keyed on the same logical field. Concatenate normalized fields;
  // the aggregator hashes on its side if it wants compactness.
  function fieldSignature(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): string {
    const name = el.getAttribute("name") || "";
    const id = el.id || "";
    const type =
      el instanceof HTMLInputElement ? (el.type || "text").toLowerCase() :
      el instanceof HTMLTextAreaElement ? "textarea" : "select";
    const placeholder = (el as HTMLInputElement).placeholder || "";
    const path = (location.pathname || "/");
    // Concatenate with a delimiter unlikely to appear in real attributes.
    return [name, id, type, placeholder, path].join("␟"); // U+241F INFO SEPARATOR ONE
  }

  function emitField(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) {
    const raw = (el as HTMLInputElement).value ?? "";
    const value = clip(raw, FIELD_VALUE_CAP);
    if (!value) return;
    // PCI backstop: if the value itself is a Luhn-valid card number, never ship
    // it — even if the field's metadata slipped past shouldCaptureField.
    if (looksLikeCardNumber(value)) return;
    if (fieldLastValue.get(el) === value) return; // dedupe identical re-emits
    fieldLastValue.set(el, value);
    const sig = fieldSignature(el);
    const seenHash = fieldHash(sig, value);
    if (fieldAlreadySent(seenHash)) return;        // same field + value already sent this session
    markFieldSent(seenHash);

    const type =
      el instanceof HTMLInputElement ? (el.type || "text").toLowerCase() :
      el instanceof HTMLTextAreaElement ? "textarea" : "select";
    const ev = {
      t: "fi",
      ts: relTs(),
      nm: el.getAttribute("name") || null,
      fid: el.id || null,
      ty: type,
      ph: (el as HTMLInputElement).placeholder ? clip((el as HTMLInputElement).placeholder, FIELD_TEXT_CAP) : null,
      lb: findLabel(el),
      url: location.pathname || "/",
      sig,
      v: value,
    } as const;
    push(ev as Parameters<typeof push>[0]);
  }

  function scheduleEmit(el: Element) {
    if (!shouldCaptureField(el)) return;
    const prior = fieldTimers.get(el);
    if (prior) window.clearTimeout(prior);
    const id = window.setTimeout(() => emitField(el), FIELD_IDLE_MS);
    fieldTimers.set(el, id);
  }

  document.addEventListener("input", (e) => {
    const t = e.target as Element | null;
    if (t) scheduleEmit(t);
  }, true);

  // Periodic safety-net scan. Some frameworks render canvas-based or
  // shadow-DOM inputs that don't bubble standard input events to document;
  // others null out the value via property setters in ways our listeners
  // miss. Every PERIODIC_SCAN_MS we iterate the visible inputs, skip ones
  // unchanged since the last emit, and queue a fresh fi for the rest. This
  // is cheap — the WeakMap dedupe already prevents redundant emits.
  const PERIODIC_SCAN_MS = 5000;
  window.setInterval(() => {
    if (document.hidden) return; // page in background, don't bother
    const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "input, textarea, select"
    );
    for (let i = 0; i < inputs.length; i++) {
      const el = inputs[i];
      if (!el) continue;
      if (!shouldCaptureField(el)) continue;
      // Dropdowns always HAVE a value (their default), so the scan re-sent every
      // untouched <select> on every page view — 41% of all field rows. A select
      // is only captured when the visitor actually changes it (input event).
      if (el instanceof HTMLSelectElement) continue;
      if (!el.value) continue;
      // Skip if the last-emitted value already matches — emitField also
      // does this check, but bailing here avoids touching the field timer
      // map for inputs that didn't change.
      if (fieldLastValue.get(el) === el.value) continue;
      emitField(el);
    }
  }, PERIODIC_SCAN_MS);
  // Blur flushes immediately — the visitor has clearly moved on.
  document.addEventListener("blur", (e) => {
    const t = e.target as Element | null;
    if (!t || !shouldCaptureField(t)) return;
    const prior = fieldTimers.get(t);
    if (prior) window.clearTimeout(prior);
    emitField(t);
  }, true);
  // Change covers <select> (which never emits "input" for option pick on
  // some browsers) and IME composition end.
  document.addEventListener("change", (e) => {
    const t = e.target as Element | null;
    if (!t || !shouldCaptureField(t)) return;
    emitField(t);
  }, true);

  // --- conversion-by-content (success text patterns) -----------------
  // For sites whose success state lives on the same URL as the form (UUID
  // forms, SPA in-place success), URL-based conversion can't fire. We
  // fetch the site's configured success text patterns once at startup,
  // then watch document.body via MutationObserver. When ANY pattern's
  // substring appears (case-insensitive) we fire a cv event ONCE per
  // session — additional matches are no-ops so a confirmation panel that
  // re-renders doesn't double-count.
  let successPatterns: string[] = [];
  let conversionFired = false;
  let scanScheduled = false;
  function scanForSuccess() {
    if (conversionFired || !successPatterns.length) return;
    scanScheduled = false;
    // Read once. document.body.innerText is what the user sees (excludes
    // hidden text), which is what we want. textContent would catch DOM
    // text the user can't read.
    const text = (document.body?.innerText || "").toLowerCase();
    if (!text) return;
    for (const p of successPatterns) {
      const idx = text.indexOf(p.toLowerCase());
      if (idx >= 0) {
        conversionFired = true;
        const around = text.slice(Math.max(0, idx - 20), idx + p.length + 20);
        const ev = {
          t: "cv",
          ts: relTs(),
          pattern: p,
          url: location.pathname || "/",
          snippet: around,
        } as const;
        push(ev as Parameters<typeof push>[0]);
        // Stop observing once fired — no point keeping the MO alive.
        try { mo?.disconnect(); } catch { /* swallow */ }
        return;
      }
    }
  }
  function scheduleScan() {
    if (conversionFired || scanScheduled) return;
    scanScheduled = true;
    // requestIdleCallback when available, otherwise a coarse setTimeout —
    // we want to coalesce many DOM mutations into a single text scan
    // rather than re-reading innerText on every mutation.
    type IdleCb = (cb: () => void, opts?: { timeout?: number }) => number;
    const ric = (window as Window & typeof globalThis & { requestIdleCallback?: IdleCb }).requestIdleCallback;
    if (typeof ric === "function") ric(scanForSuccess, { timeout: 400 });
    else window.setTimeout(scanForSuccess, 250);
  }
  let mo: MutationObserver | null = null;
  // Fetch config once (cached at the edge for 5 min). We use the same
  // origin the tracker batches go to so any CORS/connectivity that already
  // works for ingest works for this too.
  (async () => {
    // Legacy v=1 installs resolve the site by hostname and have no siteId here;
    // "/c/undefined" would 404 on every pageview. Skip config until we have one.
    if (!siteId) return;
    try {
      const r = await fetch(__HOTMAPS_INGEST__ + "/c/" + encodeURIComponent(siteId), { credentials: "omit" });
      if (!r.ok) return;
      const j = await r.json() as { successPatterns?: unknown; popups?: unknown };
      // Intervention layer: load enabled popups (popup.js reads
      // window.__HM_POPUPS__). This is the production delivery path that lets
      // popups — including the R2 risk-triggered ones — actually fire on the
      // visitor's page. Safe-by-default: only inject popup.js if there's ≥1.
      if (Array.isArray(j.popups) && j.popups.length) {
        loadPopups(j.popups);
      }
      if (Array.isArray(j.successPatterns)) {
        successPatterns = j.successPatterns
          .filter((s): s is string => typeof s === "string")
          .map((s) => s.trim())
          .filter((s) => s.length >= 2 && s.length <= 80)
          .slice(0, 20);
      }
      if (!successPatterns.length) return;
      // Run an initial scan in case the success state was already on screen
      // when the tracker booted (rare but possible — page reloaded on
      // success).
      scheduleScan();
      mo = new MutationObserver(() => scheduleScan());
      mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    } catch { /* swallow — content conversion is best-effort */ }
  })();

  // Set window.__HM_POPUPS__ and inject popup.js once. popup.js is served from
  // the SAME origin as this tracker bundle (the CDN worker), so we derive that
  // origin from our own <script> src rather than hard-coding it.
  function loadPopups(popups: unknown[]): void {
    try {
      const W2 = window as unknown as { __HM_POPUPS__?: unknown[]; __hmPopupLoaded?: boolean };
      if (W2.__hmPopupLoaded) return;
      W2.__hmPopupLoaded = true;
      W2.__HM_POPUPS__ = popups;
      // Resolve the CDN origin from the executing/located tracker script.
      let origin = "";
      try {
        const src = (scriptEl && scriptEl.src) ||
          document.querySelector<HTMLScriptElement>('script[src*="/t.js"]')?.src || "";
        if (src) origin = new URL(src, location.href).origin;
      } catch { /* fall through */ }
      if (!origin) {
        try { origin = new URL(__HOTMAPS_INGEST__).origin.replace("ingest.", "cdn."); } catch { /* give up */ }
      }
      if (!origin) return;
      const s = document.createElement("script");
      s.src = origin + "/popup.js";
      s.async = true;
      (document.body || document.head || document.documentElement).appendChild(s);
    } catch { /* never break the host page over an intervention popup */ }
  }

  // --- Abandonment Risk Engine (R1) -----------------------------------
  // The live intent-to-leave score. It owns its own passive listeners
  // (cursor/scroll/idle) and we feed it the discrete signals detected
  // elsewhere (rage/dead/error/network/field-abandon). Emits `risk` events
  // into the same buffer and publishes window.__hmRisk for popup.js (R2).
  const risk = registerRiskEngine(sessionStart, (ev) => push(ev));

  // --- frustration signals + error capture ----------------------------
  // Both live in their own modules so the tracker entrypoint stays
  // readable. They share the same buffer via push(). We tee each signal
  // into the risk engine so frustration raises the live score.
  registerSignalDetectors(sessionStart, (ev) => {
    push(ev);
    if (ev.t === "rage") risk.notify("rage");
    else if (ev.t === "dead") risk.notify("dead");
  });
  // Ingest host — used to ignore the tracker's OWN failed requests as a risk
  // signal. Ad-blockers commonly block analytics ingest; without this filter a
  // blocked HotMaps beacon would raise the visitor's risk score and fire a
  // recovery popup because *we* were being blocked, not because they're leaving.
  let ingestHost = "";
  const ownHosts: string[] = [];
  try {
    ingestHost = new URL(__HOTMAPS_INGEST__).host;      // ingest.<base>
    const base = ingestHost.replace(/^ingest\./, "");   // <base>
    ownHosts.push(ingestHost, "cdn." + base, "live." + base, "api." + base);
    // Sibling-product infra (NAGISOT) rides the same tag — its blocked/failed
    // beacons are OUR noise, never the customer's broken element.
    ownHosts.push("api.nagis.doogree.co.il", "widget.nagis.doogree.co.il");
  } catch { /* leave empty */ }
  // True when a URL points at HotMaps's OWN telemetry infra — never treat those
  // as the customer's broken network elements (see errors.ts).
  const isOwnUrl = (u: string): boolean => {
    try { return ownHosts.indexOf(new URL(u, location.href).host) >= 0; }
    catch { return ownHosts.some((h) => h && u.indexOf(h) >= 0); }
  };
  registerErrorCapture(sessionStart, (ev) => {
    push(ev);
    if (ev.t === "err") risk.notify("error");
    else if (ev.t === "net") {
      if (!(ingestHost && typeof ev.url === "string" && ev.url.indexOf(ingestHost) >= 0)) {
        risk.notify("network");
      }
    }
  }, isOwnUrl);

  // --- full console capture (opt-in) -----------------------------------
  // Enabled per site: the unified loader forwards module config
  // `console:true` as data-console="1" on the injected tag; a direct t.js
  // embed can set the same attribute by hand. Off = zero patching.
  // currentScript can be null for dynamically-injected scripts on some
  // paths (same story as resolveSiteId), so fall back to scanning for the
  // attribute on any data-site script tag.
  const consoleFlag =
    scriptEl?.dataset.console ??
    (() => {
      try {
        return document.querySelector<HTMLScriptElement>("script[data-site][data-console]")
          ?.dataset.console;
      } catch {
        return undefined;
      }
    })();
  if (consoleFlag === "1" || consoleFlag === "true") {
    registerConsoleCapture(sessionStart, (ev) => push(ev));
  }

  // --- event capture: touch (v3) ---------------------------------------
  // On mobile, click fires but tap-specific gestures don't show up as
  // dedicated signals. We track tap (touchstart-only short), long-press,
  // swipe (touchstart→touchend with distance), pinch (two-finger scale).
  let touchStart: { t: number; x: number; y: number; dist: number } | null = null;
  let longPressTimer: number | undefined;
  const LONG_PRESS_MS = 600;
  const SWIPE_MIN_PX = 30;

  function distBetween(a: Touch, b: Touch): number {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  document.addEventListener(
    "touchstart",
    (e) => {
      if (e.touches.length === 2) {
        // pinch baseline — set initial distance, the touchend reports scale.
        const a = e.touches[0]!;
        const b = e.touches[1]!;
        touchStart = {
          t: Date.now(),
          x: (a.clientX + b.clientX) / 2,
          y: (a.clientY + b.clientY) / 2,
          dist: distBetween(a, b),
        };
        return;
      }
      const t0 = e.touches[0];
      if (!t0) return;
      touchStart = { t: Date.now(), x: t0.clientX, y: t0.clientY, dist: 0 };
      // arm long-press
      if (longPressTimer !== undefined) clearTimeout(longPressTimer);
      const tx = t0.clientX;
      const ty = t0.clientY;
      const target = e.target as Element | null;
      longPressTimer = window.setTimeout(() => {
        const ev: HmTouchEvent = {
          t: "tch",
          ts: relTs(),
          kind: "long",
          x: tx,
          y: ty,
          px: tx + window.scrollX,
          py: ty + window.scrollY,
          dur: LONG_PRESS_MS,
          tag: target?.tagName.toLowerCase(),
          sel: target ? shortSelector(target) : undefined,
        };
        push(ev);
      }, LONG_PRESS_MS);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    "touchend",
    (e) => {
      if (longPressTimer !== undefined) {
        clearTimeout(longPressTimer);
        longPressTimer = undefined;
      }
      if (!touchStart) return;
      const dur = Date.now() - touchStart.t;
      // Pinch: two-finger session — touchend on second finger has touches=1,
      // we report when changedTouches gives both released distances.
      if (e.changedTouches.length === 2 && touchStart.dist > 0) {
        const a = e.changedTouches[0]!;
        const b = e.changedTouches[1]!;
        const dist = distBetween(a, b);
        const ev: HmTouchEvent = {
          t: "tch",
          ts: relTs(),
          kind: "pinch",
          x: touchStart.x,
          y: touchStart.y,
          px: touchStart.x + window.scrollX,
          py: touchStart.y + window.scrollY,
          dur,
          scale: touchStart.dist > 0 ? dist / touchStart.dist : 1,
        };
        push(ev);
        touchStart = null;
        return;
      }
      const t0 = e.changedTouches[0];
      if (!t0) {
        touchStart = null;
        return;
      }
      const dx = t0.clientX - touchStart.x;
      const dy = t0.clientY - touchStart.y;
      const totalDist = Math.sqrt(dx * dx + dy * dy);
      const target = e.target as Element | null;
      if (totalDist >= SWIPE_MIN_PX) {
        const ev: HmTouchEvent = {
          t: "tch",
          ts: relTs(),
          kind: "swipe",
          x: touchStart.x,
          y: touchStart.y,
          px: touchStart.x + window.scrollX,
          py: touchStart.y + window.scrollY,
          dx,
          dy,
          dur,
          tag: target?.tagName.toLowerCase(),
          sel: target ? shortSelector(target) : undefined,
        };
        push(ev);
      } else if (dur < LONG_PRESS_MS) {
        // Plain tap. The corresponding click event will already fire and
        // be captured by the click handler — but a dedicated tap signal
        // lets the aggregator distinguish "tap that fired no click" from
        // "tap that fired a click", which is itself useful.
        const ev: HmTouchEvent = {
          t: "tch",
          ts: relTs(),
          kind: "tap",
          x: touchStart.x,
          y: touchStart.y,
          px: touchStart.x + window.scrollX,
          py: touchStart.y + window.scrollY,
          dur,
          tag: target?.tagName.toLowerCase(),
          sel: target ? shortSelector(target) : undefined,
        };
        push(ev);
      }
      touchStart = null;
    },
    { capture: true, passive: true }
  );

  // --- event capture: form interaction (v3) ----------------------------
  // Per-field timeline: focus → (first_type) → blur/abandon. The
  // aggregator rebuilds per-field totals from these atomic events.
  // PRIVACY: we never log the actual field value, only metadata.
  const fieldFocusedAt = new WeakMap<Element, number>();
  const fieldTyped = new WeakMap<Element, boolean>();

  function isFormField(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT") {
      const t = (el as HTMLInputElement).type;
      // Skip hidden / button-like inputs.
      return !["hidden", "submit", "reset", "button"].includes(t);
    }
    return tag === "TEXTAREA" || tag === "SELECT";
  }

  function formSelectorFor(el: Element): string | undefined {
    const form = el.closest("form");
    if (!form) return undefined;
    const id = form.getAttribute("id");
    if (id) return `form#${id}`;
    const name = form.getAttribute("name");
    if (name) return `form[name=${name}]`;
    return "form";
  }

  document.addEventListener(
    "focusin",
    (e) => {
      const target = e.target as Element | null;
      if (!isFormField(target)) return;
      fieldFocusedAt.set(target, Date.now());
      fieldTyped.set(target, false);
      const ev: FormInteractionEvent = {
        t: "form",
        ts: relTs(),
        kind: "focus",
        field: shortSelector(target),
        form: formSelectorFor(target),
        inputType: target instanceof HTMLInputElement ? target.type : target.tagName.toLowerCase(),
      };
      push(ev);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    "input",
    (e) => {
      const target = e.target as Element | null;
      if (!isFormField(target)) return;
      if (fieldTyped.get(target)) return;     // already counted first type
      fieldTyped.set(target, true);
      const ev: FormInteractionEvent = {
        t: "form",
        ts: relTs(),
        kind: "first_type",
        field: shortSelector(target),
        form: formSelectorFor(target),
      };
      push(ev);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    "focusout",
    (e) => {
      const target = e.target as Element | null;
      if (!isFormField(target)) return;
      const focusedAt = fieldFocusedAt.get(target);
      const typed = !!fieldTyped.get(target);
      const focusMs = focusedAt ? Date.now() - focusedAt : undefined;
      // Abandon = blur, didn't type. Otherwise a normal blur (still useful).
      const ev: FormInteractionEvent = {
        t: "form",
        ts: relTs(),
        kind: typed ? "blur" : "abandon",
        field: shortSelector(target),
        form: formSelectorFor(target),
        focusMs,
        typed,
        inputType: target instanceof HTMLInputElement ? target.type : target.tagName.toLowerCase(),
      };
      push(ev);
      // Risk (R1): a field the visitor TYPED into then left without submitting
      // is the highest-signal form abandonment — field-of-last-interaction is
      // diagnostic (form-analytics consensus). A bare blur with no typing is
      // far weaker, so we only charge the engine when they actually engaged.
      if (typed) risk.notify("field_abandon");
    },
    { capture: true, passive: true }
  );

  // Form-level submit. We listen on every form for the submit event;
  // a failed submit (preventDefault + missing validity) emits submit_fail.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target as HTMLFormElement | null;
      if (!form || form.tagName !== "FORM") return;
      const ok = form.checkValidity?.() ?? true;
      const ev: FormInteractionEvent = {
        t: "form",
        ts: relTs(),
        kind: ok ? "submit" : "submit_fail",
        form: form.id ? `form#${form.id}` : form.getAttribute("name") ? `form[name=${form.getAttribute("name")}]` : "form",
      };
      push(ev);
    },
    { capture: true, passive: true }
  );

  // --- event capture: Core Web Vitals (v3) -----------------------------
  // We use the PerformanceObserver API to capture LCP, FID/INP, CLS, plus
  // a TTFB + FCP read from the Navigation Timing API.
  let perf: PerformanceEvent = { t: "perf", ts: relTs() };
  let perfDirty = false;
  function flushPerf(final: boolean) {
    if (!perfDirty && !final) return;
    perfDirty = false;
    push({ ...perf, ts: relTs(), ...(final ? { final: true } : {}) });
  }
  try {
    // LCP — repeated entries; we keep the last.
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
      const t = last?.renderTime ?? last?.loadTime ?? last?.startTime;
      if (typeof t === "number") {
        perf.lcp = Math.round(t);
        perfDirty = true;
      }
    }).observe({ type: "largest-contentful-paint", buffered: true });

    // INP — track worst interaction.
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        const d = (e as PerformanceEntry & { duration?: number }).duration;
        if (typeof d === "number" && (perf.inp === undefined || d > perf.inp)) {
          perf.inp = Math.round(d);
          perfDirty = true;
        }
      }
    }).observe({ type: "event", buffered: true, durationThreshold: 16 } as PerformanceObserverInit);

    // CLS — sum unexpected layout shifts.
    let clsSum = 0;
    new PerformanceObserver((list) => {
      for (const e of list.getEntries() as Array<
        PerformanceEntry & { value: number; hadRecentInput?: boolean }
      >) {
        if (!e.hadRecentInput) clsSum += e.value;
      }
      // Store ×1000 for integer transport; aggregator divides on read.
      const next = Math.round(clsSum * 1000);
      if (perf.cls === undefined || next !== perf.cls) {
        perf.cls = next;
        perfDirty = true;
      }
    }).observe({ type: "layout-shift", buffered: true });

    // Navigation timing — TTFB + FCP from Navigation Timing v2.
    const nav = performance.getEntriesByType("navigation")[0] as
      | (PerformanceEntry & { responseStart?: number })
      | undefined;
    if (nav && typeof nav.responseStart === "number") {
      perf.ttfb = Math.round(nav.responseStart);
      perfDirty = true;
    }
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.name === "first-contentful-paint") {
          perf.fcp = Math.round(e.startTime);
          perfDirty = true;
        }
      }
    }).observe({ type: "paint", buffered: true });
  } catch {
    /* PerformanceObserver missing or restricted — fail silent. */
  }
  // Push a snapshot at ~3s after first paint (typical LCP arrival), then
  // again on every flush so values can refine. Final read fires on unload.
  setTimeout(() => flushPerf(false), 3000);
  setInterval(() => flushPerf(false), 10000);

  // --- public SDK ------------------------------------------------------
  // Customers can fire custom events + identify the visitor by calling
  // `window.hotmaps.track(...)` and `window.hotmaps.identify(...)`. The
  // SDK swallows nothing — bad input is rejected loudly to make
  // integration bugs visible in dev tools.
  function pushCustom(name: string, props?: Record<string, string | number | boolean>) {
    if (typeof name !== "string" || name.length === 0 || name.length > 100) {
      console.warn("[hotmaps] track: event name must be a 1-100 char string");
      return;
    }
    // Special handling: popup.js routes its impressions/clicks through
    // hotmaps.track("__hm_popup_event", {popupId, kind, trigger, path}).
    // We turn that into a first-class pe event so the aggregator can
    // unroll into popup_impressions without a custom-event detour.
    if (name === "__hm_popup_event" && props && typeof props.popupId === "string" && typeof props.kind === "string") {
      const ev = {
        t: "pe",
        ts: relTs(),
        popupId: String(props.popupId),
        kind: String(props.kind) as "shown" | "click" | "dismiss" | "convert",
        trigger: typeof props.trigger === "string" ? String(props.trigger) : undefined,
        path: typeof props.path === "string" ? String(props.path) : undefined,
      } as const;
      push(ev as Parameters<typeof push>[0]);
      return;
    }
    const cleanProps: Record<string, string | number | boolean> | undefined =
      props ? cleanFlatProps(props) : undefined;
    const ev: HmCustomEvent = { t: "ev", ts: relTs(), name, props: cleanProps };
    push(ev);
  }
  function pushIdentify(uid: string, traits?: Record<string, string | number | boolean>) {
    if (typeof uid !== "string" || uid.length === 0 || uid.length > 200) {
      console.warn("[hotmaps] identify: uid must be a 1-200 char string");
      return;
    }
    identifiedUid = uid;
    const ev: IdentifyEvent = {
      t: "id",
      ts: relTs(),
      uid,
      traits: traits ? cleanFlatProps(traits) : undefined,
    };
    push(ev);
  }
  // Set the visitor's lifecycle stage. Routes through the custom-event pipeline
  // as the reserved __hm_stage event; the aggregator turns it into the
  // first-class lifecycle_stage column (0028). Canonical track() events
  // (cart/checkout/purchase/…) also imply a stage, so this is only needed for
  // stages with no matching event (e.g. "interested", "returning").
  const STAGE_NAMES = ["visitor", "interested", "cart", "checkout", "purchase", "returning"];
  function pushStage(name: string) {
    const s = typeof name === "string" ? name.toLowerCase() : "";
    if (!STAGE_NAMES.includes(s)) {
      console.warn("[hotmaps] stage: must be one of " + STAGE_NAMES.join(", "));
      return;
    }
    pushCustom("__hm_stage", { stage: s });
  }
  function cleanFlatProps(
    raw: Record<string, unknown>
  ): Record<string, string | number | boolean> {
    const out: Record<string, string | number | boolean> = {};
    let count = 0;
    let totalChars = 0;
    for (const k of Object.keys(raw)) {
      if (count >= 8) break;        // hard cap: 8 keys
      const v = raw[k];
      if (typeof v === "string") {
        const s = v.slice(0, 200);
        if (totalChars + s.length > 1500) continue;
        totalChars += s.length;
        out[k] = s;
      } else if (typeof v === "number" && Number.isFinite(v)) {
        out[k] = v;
      } else if (typeof v === "boolean") {
        out[k] = v;
      } else {
        continue;  // skip non-primitive
      }
      count++;
    }
    return out;
  }
  // Public flush — push pending rrweb (so a large FullSnapshot isn't stranded on
  // a WebView teardown) then send via the normal fetch path (not sendBeacon,
  // which has a 64KB cap). Callable from a native host on background/pause.
  function publicFlush() {
    try {
      if (rrwebPending.length) {
        push({ t: "rr", ts: relTs(), events: rrwebPending });
        rrwebPending = [];
      }
      flush("timer");
    } catch { /* never throw from flush */ }
  }
  W.hotmaps = {
    track: pushCustom,
    stage: pushStage,
    identify: pushIdentify,
    ready: () => true,
    flush: publicFlush,
    getSessionId: () => sessionId,
    getVisitorId: () => visitorId,
  };
  // Drain any queued calls made before the SDK was loaded.
  if (Array.isArray(W.__hotmapsQueue)) {
    for (const item of W.__hotmapsQueue) {
      try {
        if (item.m === "track") pushCustom(item.a[0] as string, item.a[1] as Record<string, string | number | boolean> | undefined);
        else if (item.m === "stage") pushStage(item.a[0] as string);
        else if (item.m === "identify") pushIdentify(item.a[0] as string, item.a[1] as Record<string, string | number | boolean> | undefined);
      } catch {
        /* per-item failures shouldn't poison the drain */
      }
    }
    W.__hotmapsQueue = [];
  }

  // --- lifecycle: flush on unload --------------------------------------
  // visibilitychange + pagehide cover most cases. beforeunload is unreliable
  // on mobile so we don't depend on it as the primary signal.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPerf(true);      // final perf snapshot before unload
      flush("unload");
    }
  });
  window.addEventListener("pagehide", () => {
    flushPerf(true);
    flush("unload");
  });

  // --- helpers ----------------------------------------------------------
  function resolveSiteId(current: HTMLScriptElement | null): string | undefined {
    // 1. Primary: the data-site attribute on the script that is executing
    //    right now. This works for `<script async data-site="…" src="…">`
    //    embedded in the host HTML.
    const direct = current?.dataset.site;
    if (direct && /^[a-z0-9_]{8,40}$/.test(direct)) return direct;
    // 2. Fallback: GTM and other dynamic loaders inject our script after
    //    parsing, which makes document.currentScript null. Scan every
    //    <script> with data-site and pick the first valid one. We
    //    intentionally don't filter by src host — keeps the matcher robust
    //    if customers proxy through their own CDN later.
    try {
      const tags = document.querySelectorAll<HTMLScriptElement>(
        "script[data-site]"
      );
      for (let i = 0; i < tags.length; i++) {
        const v = tags[i]?.dataset.site;
        if (v && /^[a-z0-9_]{8,40}$/.test(v)) return v;
      }
    } catch {
      /* defensive — querySelector should never throw, but if it does
         we'd rather ship v=1 than crash on the host page */
    }
    return undefined;
  }

  function uuid(): string {
    // Browser crypto.randomUUID is available widely; fallback for old browsers.
    if (typeof crypto !== "undefined" && (crypto as Crypto).randomUUID) {
      return crypto.randomUUID();
    }
    // RFC4122-ish fallback — quality good enough for IDs, not for keys.
    const r = (n: number) => Math.floor(Math.random() * n).toString(16);
    return `${r(0xffffffff)}-${r(0xffff)}-${r(0xffff)}-${r(0xffff)}-${r(0xffffffff)}${r(0xffff)}`;
  }

  // --- sessionization -------------------------------------------------------
  // A session must survive full-page navigation (WordPress reloads the page on
  // every link) AND tab-switch-away-and-back, but start fresh when the visitor
  // actually left (closed the tab) and returns, or sat idle too long.
  //
  // Store the session in BOTH stores and reconcile:
  //   • sessionStorage — per-tab, survives same-tab navigation, CLEARED when the
  //     tab is closed. This is what distinguishes "navigated/returned to the
  //     tab" (resume) from "closed and reopened" (new session).
  //   • localStorage    — carries the last-activity timestamp across the full
  //     page reload so we can enforce the idle cutoff, and lets a brand-new tab
  //     re-join an active session the visitor already has open elsewhere.
  const SESSION_KEY = "_hm_sid";
  const SESSION_TS_KEY = "_hm_sid_ts";
  const INACTIVITY_MS = 30 * 60 * 1000; // 30 min idle → next visit is a new session
  function getOrResumeSession(): { id: string; isNew: boolean } {
    const now = Date.now();
    try {
      const tabSid = sessionStorage.getItem(SESSION_KEY);      // survives nav, dies on tab close
      const lastTs = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10) || 0;
      const idle = now - lastTs;
      // Resume when THIS tab already owns a session and it hasn't gone idle.
      if (tabSid && idle < INACTIVITY_MS) { markSessionActive(tabSid, now); return { id: tabSid, isNew: false }; }
      // New tab (or reopened after close), but the visitor still has a live
      // session running (recent activity from another tab) → join it, so two
      // open tabs are one visit rather than two.
      const sharedSid = localStorage.getItem(SESSION_KEY);
      if (!tabSid && sharedSid && idle < INACTIVITY_MS) { markSessionActive(sharedSid, now); return { id: sharedSid, isNew: false }; }
      const fresh = uuid();
      markSessionActive(fresh, now);
      return { id: fresh, isNew: true };
    } catch {
      return { id: uuid(), isNew: true }; // storage blocked → best-effort per-load id
    }
  }
  function markSessionActive(sid: string, ts: number): void {
    try {
      sessionStorage.setItem(SESSION_KEY, sid);
      localStorage.setItem(SESSION_KEY, sid);
      localStorage.setItem(SESSION_TS_KEY, String(ts));
    } catch { /* ignore */ }
  }

  function getOrCreateVisitorId(): string {
    const KEY = "_hm_vid";
    try {
      const existing = localStorage.getItem(KEY);
      if (existing) return existing;
      const fresh = uuid();
      localStorage.setItem(KEY, fresh);
      return fresh;
    } catch {
      // Some browsers (Safari private mode, strict policies) throw on access.
      // Fall back to an in-memory id — the visitor will be counted as new
      // on every page load, which is acceptable for the privacy-locked-down
      // minority.
      return uuid();
    }
  }

  function documentWidth(): number {
    return Math.max(
      document.documentElement.scrollWidth,
      document.body?.scrollWidth || 0,
      document.documentElement.clientWidth
    );
  }
  function documentHeight(): number {
    return Math.max(
      document.documentElement.scrollHeight,
      document.body?.scrollHeight || 0,
      document.documentElement.clientHeight
    );
  }

  function hashPath(p: string): string {
    // Tiny, deterministic 32-bit hash → 8 hex chars. We bucket heatmaps by
    // pathname only (no query) so /search?q=a and /search?q=b share data.
    let h = 2166136261 >>> 0; // FNV-1a basis
    for (let i = 0; i < p.length; i++) {
      h ^= p.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
  }

  function shortSelector(el: Element): string {
    // Best-effort: build a 1–3 segment selector. Heavy DOM walks on click
    // would be perceptible — keep this cheap.
    const id = (el as HTMLElement).id;
    if (id) return `#${id}`.slice(0, 60);
    const tag = el.tagName.toLowerCase();
    const cls = (el.getAttribute("class") || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .join(".");
    const parent = el.parentElement;
    const parentTag = parent ? parent.tagName.toLowerCase() : "";
    const base = cls ? `${tag}.${cls}` : tag;
    return parentTag ? `${parentTag}>${base}` : base;
  }

  function truncatedText(el: Element): string | undefined {
    const t = (el as HTMLElement).innerText;
    if (!t) return undefined;
    return t.trim().replace(/\s+/g, " ").slice(0, 40) || undefined;
  }

  function approxSize(ev: TrackerEvent): number {
    // Approximate byte cost. For non-rrweb events the overhead is fixed.
    if (ev.t === "rr") {
      // rrweb chunks can be huge; estimate by serialising.
      try {
        return JSON.stringify(ev).length;
      } catch {
        return 1024;
      }
    }
    return 80;
  }

  function hookHistory(onChange: () => void) {
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) {
      const r = origPush.apply(this, args as Parameters<typeof origPush>);
      onChange();
      return r;
    };
    history.replaceState = function (...args) {
      const r = origReplace.apply(this, args as Parameters<typeof origReplace>);
      onChange();
      return r;
    };
    window.addEventListener("popstate", onChange);
  }

  // Silence unused-variable warnings when we wire these in later.
  void pageStart;
  void __HOTMAPS_VERSION__;
}
